// Local auth client. Replaces @supabase/supabase-js entirely.
// Talks to our Hetzner backend (api.lasmarichauffeurs.com) via REST.
// Stores session in localStorage. Exposes the same surface the rest of the
// frontend already uses (auth.signIn, auth.getSession, auth.onAuthStateChange,
// auth.resetPasswordForEmail, auth.updatePassword, etc.) so no other files
// needed to change.

const API_URL = (process.env.NODE_ENV === 'production' && process.env.REACT_APP_API_URL)
  ? process.env.REACT_APP_API_URL
  : (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
      ? `${window.location.protocol}//${window.location.hostname}:5050`
      : 'http://localhost:5050');

const STORAGE_KEY = 'lasmari_auth_session';

// --- Auth event bus (mirrors supabase-js onAuthStateChange) ---
const listeners = new Set();
function emit(event, session) {
  for (const fn of listeners) {
    try { fn(event, session); } catch (err) { console.error('[auth] listener error:', err); }
  }
}

// --- Session storage ---
function getStoredSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (s.expires_at && (Date.now() / 1000) > s.expires_at) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return s;
  } catch {
    return null;
  }
}
function setStoredSession(session) {
  if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  else localStorage.removeItem(STORAGE_KEY);
}

// --- JWT payload decoder (no validation — server already verified on issue) ---
function decodeJWT(token) {
  try {
    const payload = token.split('.')[1];
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - b64.length % 4) % 4);
    const json = decodeURIComponent(atob(padded).split('').map((c) =>
      '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    ).join(''));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function buildSessionFromLogin(token, user) {
  const decoded = decodeJWT(token);
  const meta = { role: user.role || 'admin', approval_status: user.approval_status || 'approved' };
  return {
    access_token: token,
    refresh_token: null,
    expires_at: decoded?.exp,
    token_type: 'bearer',
    user: {
      id: user.id,
      email: user.email,
      user_metadata: meta,
      raw_user_meta_data: meta
    }
  };
}

// --- HTTP helper ---
async function apiFetch(path, options = {}) {
  let res;
  try {
    res = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {})
      }
    });
  } catch (err) {
    return { data: null, error: { message: err.message || 'Network error', status: 0 } };
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { data: null, error: { message: body.error || body.message || `HTTP ${res.status}`, status: res.status } };
  }
  return { data: body, error: null };
}

// --- Detect password-recovery hash from email link on page load ---
function consumeRecoveryHash() {
  if (typeof window === 'undefined') return;
  const hash = window.location.hash;
  if (!hash || !hash.includes('access_token=')) return;
  const params = new URLSearchParams(hash.slice(1));
  const token = params.get('access_token');
  const type = params.get('type');
  if (token && type === 'recovery') {
    const decoded = decodeJWT(token);
    if (decoded && decoded.type === 'recovery') {
      const session = {
        access_token: token,
        refresh_token: null,
        expires_at: decoded.exp,
        token_type: 'bearer',
        recovery: true,
        user: {
          id: decoded.sub,
          email: decoded.email,
          user_metadata: { role: 'admin', approval_status: 'approved' },
          raw_user_meta_data: { role: 'admin', approval_status: 'approved' }
        }
      };
      setStoredSession(session);
      // Strip hash so refresh doesn't re-trigger
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      setTimeout(() => emit('PASSWORD_RECOVERY', session), 0);
    }
  }
}
if (typeof window !== 'undefined') consumeRecoveryHash();

// --- Public client (mimics @supabase/supabase-js shape) ---
const supabase = {
  isInitialized: true,
  client: null,
  isConnected() { return true; },

  async isAuthenticated() {
    const s = getStoredSession();
    return s !== null && !s.recovery;
  },

  async isAdmin() {
    const s = getStoredSession();
    if (!s || !s.user) return false;
    const role = s.user.user_metadata?.role || s.user.raw_user_meta_data?.role;
    const status = s.user.user_metadata?.approval_status || s.user.raw_user_meta_data?.approval_status;
    return role === 'admin' && (!status || status === 'approved');
  },

  get auth() {
    return {
      async signInWithPassword({ email, password }) {
        const { data, error } = await apiFetch('/api/auth/admin/login', {
          method: 'POST',
          body: JSON.stringify({ email, password })
        });
        if (error) return { data: null, error };
        const session = buildSessionFromLogin(data.token, data.user);
        setStoredSession(session);
        setTimeout(() => emit('SIGNED_IN', session), 0);
        return { data: { user: session.user, session }, error: null };
      },

      async signUp({ email, password, options }) {
        const name = options?.data?.name;
        const { data, error } = await apiFetch('/api/auth/admin/signup', {
          method: 'POST',
          body: JSON.stringify({ email, password, name })
        });
        if (error) return { data: null, error };
        // signUp does NOT create a session (pending approval gate)
        return { data: { user: data.user, session: null }, error: null };
      },

      async signOut() {
        setStoredSession(null);
        setTimeout(() => emit('SIGNED_OUT', null), 0);
        return { error: null };
      },

      async getSession() {
        return { data: { session: getStoredSession() }, error: null };
      },

      async getUser() {
        const s = getStoredSession();
        return { data: { user: s?.user || null }, error: null };
      },

      onAuthStateChange(callback) {
        listeners.add(callback);
        const s = getStoredSession();
        // Fire INITIAL_SESSION asynchronously so listeners attached in same tick
        // still receive it.
        setTimeout(() => {
          if (s?.recovery) callback('PASSWORD_RECOVERY', s);
          else if (s) callback('SIGNED_IN', s);
          else callback('INITIAL_SESSION', null);
        }, 0);
        return {
          data: {
            subscription: {
              unsubscribe() { listeners.delete(callback); }
            }
          },
          error: null
        };
      },

      async resetPasswordForEmail(email, options = {}) {
        const { error } = await apiFetch('/api/auth/admin/forgot-password', {
          method: 'POST',
          body: JSON.stringify({ email, redirectTo: options?.redirectTo })
        });
        if (error) return { data: null, error };
        return { data: {}, error: null };
      },

      async updateUser({ password }) {
        const s = getStoredSession();
        if (!s) return { data: null, error: { message: 'Not authenticated' } };
        const { error } = await apiFetch('/api/auth/admin/update-password', {
          method: 'POST',
          headers: { Authorization: `Bearer ${s.access_token}` },
          body: JSON.stringify({ password })
        });
        if (error) return { data: null, error };
        // After recovery flow, clear session so user must log in fresh
        if (s.recovery) setStoredSession(null);
        return { data: { user: s.user }, error: null };
      }
    };
  },

  from() { throw new Error('Frontend should not query DB directly — call backend API endpoints instead.'); },
  get storage() { throw new Error('Storage not available — feature not used in this app.'); },
  channel() { throw new Error('Realtime channels not available — feature not used in this app.'); },
  rpc() { throw new Error('Frontend should not call RPC directly — call backend API endpoints instead.'); }
};

// --- Auth helpers (kept identical to previous version so consumers don't change) ---
export const auth = {
  async signIn(email, password) {
    console.log('🔐 Auth.signIn called with:', email);
    try {
      const result = await supabase.auth.signInWithPassword({ email, password });
      if (result.error) {
        console.error('❌ Auth failed:', result.error.message);
        return result;
      }
      // Backend already enforces approval_status — this is a defensive double-check.
      const user = result.data?.user;
      if (user) {
        const approvalStatus = user.user_metadata?.approval_status || user.raw_user_meta_data?.approval_status;
        if (approvalStatus === 'pending') {
          await supabase.auth.signOut();
          return { data: null, error: new Error('Your account is pending admin approval. Please wait for approval before signing in.') };
        }
        if (approvalStatus === 'rejected') {
          await supabase.auth.signOut();
          return { data: null, error: new Error('Your account request has been rejected. Please contact support.') };
        }
      }
      localStorage.removeItem('admin_token');
      console.log('✅ Auth successful');
      return result;
    } catch (error) {
      console.error('❌ Error during signIn:', error);
      return { data: null, error };
    }
  },

  async signOut() {
    console.log('🚪 Auth.signOut called');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('user_data');
    return await supabase.auth.signOut();
  },

  async getSession() { return await supabase.auth.getSession(); },
  async getUser()    { return await supabase.auth.getUser(); },
  async isAdmin()    { return await supabase.isAdmin(); },

  async resetPasswordForEmail(email, redirectTo) {
    console.log('🔁 Auth.resetPasswordForEmail called with:', email, '→', redirectTo);
    return await supabase.auth.resetPasswordForEmail(email, { redirectTo });
  },

  async updatePassword(newPassword) {
    console.log('🔑 Auth.updatePassword called');
    return await supabase.auth.updateUser({ password: newPassword });
  },

  onAuthStateChange(callback) {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(callback);
    return () => subscription.unsubscribe();
  },

  async createAdminUser(email, password) {
    console.log('👨‍💼 Creating admin user:', email);
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { data: { role: 'admin', approval_status: 'pending', requested_at: new Date().toISOString() } }
    });
    if (error) {
      console.error('❌ Failed to create admin user:', error);
      throw error;
    }
    console.log('✅ Admin user created — pending approval');
    return data;
  }
};

export default supabase;
